import { useEffect, useMemo, useState } from 'react';
import { Check, ClipboardList, Copy, Loader2, Search } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { collection, limit, onSnapshot, orderBy, query, Timestamp } from 'firebase/firestore';
import { db, functions } from '../../config/firebase';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

type WaitlistRole = 'shopper' | 'retailer' | 'unknown';
type WaitlistStatus = 'pending' | 'approved';
type StatusFilter = 'waiting' | 'approved' | 'all';

interface WaitlistEntry {
  id: string;
  email: string;
  name: string;
  role: WaitlistRole;
  city: string;
  businessName: string;
  status: WaitlistStatus;
  createdAt: Date | null;
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

function formatJoined(date: Date | null): string {
  if (!date) return '—';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function roleLabel(role: WaitlistRole): string {
  if (role === 'retailer') return 'Retailer';
  if (role === 'shopper') return 'Shopper';
  return 'Not specified';
}

export function AdminWaitlist() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('waiting');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'waitlist'), orderBy('createdAt', 'desc'), limit(500));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEntries(
          snap.docs.map((d) => {
            const data = d.data();
            const role = data.role === 'retailer' || data.role === 'shopper' ? data.role : 'unknown';
            return {
              id: d.id,
              email: (data.email as string) || '',
              name: (data.name as string) || '',
              role,
              city: (data.city as string) || '',
              businessName: (data.businessName as string) || '',
              status: data.status === 'approved' ? 'approved' : 'pending',
              createdAt: toDate(data.createdAt),
            };
          }),
        );
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('[AdminWaitlist] snapshot error:', err);
        setError(err.message || 'Failed to load waitlist');
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  const waitingCount = entries.filter((entry) => entry.status !== 'approved').length;

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return entries.filter((entry) => {
      if (statusFilter === 'waiting' && entry.status === 'approved') return false;
      if (statusFilter === 'approved' && entry.status !== 'approved') return false;
      if (!term) return true;
      return [entry.email, entry.name, entry.businessName, entry.city]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [entries, searchTerm, statusFilter]);

  const copyEmail = async (entry: WaitlistEntry) => {
    try {
      await navigator.clipboard.writeText(entry.email);
      setCopiedId(entry.id);
      window.setTimeout(() => setCopiedId((current) => (current === entry.id ? null : current)), 1500);
    } catch (err) {
      console.error('[AdminWaitlist] copy failed:', err);
    }
  };

  const approve = async (entry: WaitlistEntry) => {
    setApprovingId(entry.id);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'approveWaitlist');
      await fn({ entryId: entry.id });
    } catch (err: any) {
      console.error('[AdminWaitlist] approve failed:', err);
      setError(err?.message || 'Could not approve this request');
    } finally {
      setApprovingId(null);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Waitlist</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">
            Allow login creates their account and emails a link to set a password.
          </p>
        </div>
        <div className="text-xs sm:text-sm text-body">
          {loading ? 'Loading…' : `${waitingCount} waiting`}
        </div>
      </div>

      <Card>
        <CardContent className="p-3 sm:p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search name, email, shop, or city"
              className="pl-9 bg-gray-50 border-gray-200"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {([
              ['waiting', 'Waiting'],
              ['approved', 'Approved'],
              ['all', 'All'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setStatusFilter(id)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium',
                  statusFilter === id ? 'bg-primary text-white' : 'bg-gray-100 text-body',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center">
              <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-heading font-medium">
                {entries.length === 0 ? 'No waitlist requests yet' : 'No matching requests'}
              </p>
              <p className="text-body text-sm mt-1">
                {entries.length === 0
                  ? 'New landing page signups will show up here.'
                  : 'Try another search or filter.'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((entry) => {
                const title = entry.name || entry.email;
                const details = [
                  entry.name ? entry.email : '',
                  entry.businessName,
                  entry.city,
                  formatJoined(entry.createdAt),
                ].filter(Boolean);

                return (
                  <li key={entry.id} className="flex items-center gap-3 px-4 py-3 sm:px-6">
                    <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {(title.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-heading truncate">{title}</p>
                        <span
                          className={cn(
                            'text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0',
                            entry.role === 'retailer'
                              ? 'bg-blue-100 text-blue-700'
                              : entry.role === 'shopper'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-gray-100 text-gray-600',
                          )}
                        >
                          {roleLabel(entry.role)}
                        </span>
                      </div>
                      <p className="text-xs text-body truncate">{details.join(' · ')}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyEmail(entry)}
                      className="p-2 text-gray-400 hover:text-heading hover:bg-gray-100 rounded-lg flex-shrink-0"
                      title="Copy email"
                    >
                      <Copy className="h-4 w-4" />
                      <span className="sr-only">{copiedId === entry.id ? 'Copied' : 'Copy email'}</span>
                    </button>
                    {entry.status === 'approved' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2.5 py-1 rounded-full flex-shrink-0">
                        <Check className="h-3.5 w-3.5" />
                        Approved
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        disabled={approvingId === entry.id}
                        onClick={() => approve(entry)}
                        className="flex-shrink-0"
                      >
                        {approvingId === entry.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          'Allow login'
                        )}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
